type CapturedParams = {
  customHeaderText?: string;
  customSubtext?: string;
  sparkId?: string;
  displayUploadModal?: boolean;
  forceOnAllUsers?: boolean;
  forceOnZeroUploadsOnly?: boolean;
  /** Pathname that wrote this capture. A later page must not inherit it. */
  capturedPath?: string;
};

const STORAGE_KEY = 'sparklink_upload_modal_params';

export const sparklinkParams = {
  get: (): CapturedParams | null => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  set: (params: CapturedParams | null): void => {
    try {
      if (params) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...params, capturedPath: window.location.pathname }));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  },

  clear: (): void => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  },
};

export function sparklinkUploadModalLandingKey(sparkId: string | undefined): string {
  return sparkId ?? 'generic';
}

/**
 * Session capture is shared by every page that hosts the uploader, and it outlives the
 * URL strip. It applies on the page that wrote it (and that page's child route, so
 * `/chat` → `/chat/:conversationId` stays one landing). A capture with no path was
 * written when only the Assistant read this key.
 */
export function sparklinkCapturedParamsMatchPath(params: CapturedParams, pathname: string): boolean {
  const capturedPath = params.capturedPath;
  if (!capturedPath) {
    return pathname === '/chat' || pathname.startsWith('/chat/');
  }
  return pathname === capturedPath || pathname.startsWith(`${capturedPath}/`);
}

// Page-session only, in memory — never persisted. A sparklink click is a
// fresh document load, so this Set starts empty and the uploader opens
// again every time the link is clicked, even for the same spark_id. It
// exists only to stop an immediate re-open loop right after a dismissal,
// since the hosting Chat page can remount mid-flow (e.g. the redirect from
// `/chat` to `/chat/:conversationId`) while the sparklink params are still
// live in sessionStorage.
const dismissedSparklinkUploadModalLandings = new Set<string>();

export function wasSparklinkUploadModalDismissed(landingKey: string): boolean {
  return dismissedSparklinkUploadModalLandings.has(landingKey);
}

export function markSparklinkUploadModalDismissed(landingKey: string): void {
  dismissedSparklinkUploadModalLandings.add(landingKey);
}

export function resetSparklinkUploadModalDismissals(): void {
  dismissedSparklinkUploadModalLandings.clear();
}

/**
 * Eagerly persist upload-modal sparklink params before URL strip (e.g. useConversationChat redirect).
 * No-op unless display_upload_modal=true.
 */
export function captureSparklinkUploadModalParams(params: URLSearchParams): void {
  if (params.get('display_upload_modal') !== 'true') {
    return;
  }

  sparklinkParams.set({
    customHeaderText: params.get('modal_header_text') || undefined,
    customSubtext: params.get('modal_header_subtext') || undefined,
    sparkId: params.get('spark_id') || undefined,
    displayUploadModal: true,
    forceOnAllUsers: params.get('force_uploader_on_all_users') === 'true' ? true : undefined,
    forceOnZeroUploadsOnly: params.get('force_uploader_when_zero_uploads_only') === 'true' ? true : undefined,
  });
}

export type { CapturedParams };
